// Import from config
import { CLOUD_FUNCTIONS_URL } from "../config/environment";
import { cloudFunctionsConfig } from "../config/cloudFunctions";
import { V2_FUNCTION_URLS, shouldUseV2Functions } from "../config/v2FunctionUrls";
import { logger } from "./logger/BrowserLogger";
import { UserMessages } from "./logger/UserMessages";
import { auth } from "../config/firebase";
import { getIdToken } from 'firebase/auth';

export type ProgrammingLanguage = 'python' | 'typescript' | 'java' | 'golang';

export interface WalkthroughStep {
  index: number;
  value: string;
  operation: string;
  state: string;
  explanation: string;
  subarray?: string;
}

export interface AlgorithmWalkthrough {
  id: string;
  approach: string;
  title: string;
  description: string;
  inputData: string;
  expectedOutput: string;
  steps: WalkthroughStep[];
  keyObservations: string[];
  complexity: {
    time: string;
    space: string;
  };
  createdAt: Date;
}

export interface BatchGenerationResult {
  success: boolean;
  generated: { [leetcodeNumber: number]: Problem };  // Newly generated problems
  existing: { [leetcodeNumber: number]: Problem };   // Already existed problems
  failed: Array<{ number: number; error: string }>;  // Failed to generate
  message: string;
}

export interface Problem {
  id: string;
  title: string;
  frequency: number;
  leetcode_number?: number;
  leetcodeUrl?: string;
  companies: Array<{
    name: string;
    frequency: number;
    acceptance_rate: number;
  }>;
  pattern: {
    name: string;
    description: string;
    keyPoints: string[];
  };
  description: {
    problem: string;
    examples: Array<{
      input: string;
      output: string;
      explanation?: string;
    }>;
    constraints: string[];
    followUp?: string;
  };
  algorithm: {
    approaches: Array<{
      name: string;
      steps: string[];
      insights?: string[];
      complexity: {
        time: string;
        space: string;
        notes?: string;
      };
    }>;
  };
  code: {
    implementations: {
      python: Array<{
        name: string;
        code: string;
        explanation?: string;
      }>;
      typescript?: Array<{
        name: string;
        code: string;
        explanation?: string;
      }>;
      java?: Array<{
        name: string;
        code: string;
        explanation?: string;
      }>;
      golang?: Array<{
        name: string;
        code: string;
        explanation?: string;
      }>;
    };
  };
  tags: string[];
  difficulty: "Easy" | "Medium" | "Hard";
  walkthroughs?: {
    [approachName: string]: AlgorithmWalkthrough[];
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface GetProblemsRequest {
  page?: number;
  limit?: number;
  tags?: string[];
  difficulty?: string[];
  search?: string;
  company?: string;
}

export interface GenerateProblemRequest {
  topic: string;
  difficulty?: "Easy" | "Medium" | "Hard";  // Optional - backend determines appropriate difficulty if not specified
  pattern?: string;
}

export interface ProblemStats {
  totalProblems: number;
  difficultyStats: {
    Easy: number;
    Medium: number;
    Hard: number;
  };
  topTags: Array<{
    tag: string;
    count: number;
  }>;
  averageFrequency: number;
}

export interface SolutionFeedback {
  correctness: {
    isCorrect: boolean;
    score: number;
    explanation: string;
  };
  codeQuality?: {
    score: number;
    readability: string;
    bestPractices: string[];
    improvements: string[];
  };
  complexity: {
    timeComplexity: string;
    spaceComplexity: string;
    analysis: string;
    isOptimal: boolean;
  };
  edgeCases: {
    handledCorrectly: boolean;
    missedCases: string[];
    suggestions: string[];
  };
  overallScore: number;
  summary: string;
}

export interface VerifySolutionRequest {
  solution: string;
  solutionType: "code" | "algorithm" | "drawing" | "notes";
}

export interface CsvUploadResponse {
  success: boolean;
  sessionId: string;
  totalRows: number;
  message: string;
}

export interface CsvProcessBatchRequest {
  sessionId: string;
  batchSize?: number;
  autoProcess?: boolean;
}

export interface CsvProcessBatchResponse {
  success: boolean;
  processed: number;
  remaining: number;
  failed: number;
  newProblems: number;
  updatedProblems: number;
  completed: boolean;
  errors?: string[];
  summary?: {
    totalProcessed: number;
    totalNew: number;
    totalUpdated: number;
    totalFailed: number;
  };
}

export interface SaveUserSolutionRequest {
  solutionCode: string;
  solutionType: 'code' | 'algorithm' | 'drawing' | 'notes';
  notes?: string;
}

export interface UserSolution {
  id: string;
  userId: string;
  problemId: string;
  solutionCode: string;
  solutionType: 'code' | 'algorithm' | 'drawing' | 'notes';
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}


export interface UserCompletionStatus {
  id: string;
  userId: string;
  problemId: string;
  isCompleted: boolean;
  completedAt: Date;
  updatedAt: Date;
}

export interface CompletionStatusResponse {
  success: boolean;
  data: UserCompletionStatus;
  isCompleted: boolean;
  message?: string;
}

export interface CompletedProblemsResponse {
  success: boolean;
  data: UserCompletionStatus[];
  count: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatHistory {
  id: string;
  userId: string;
  problemId: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ChatRequest {
  message: string;
  problemId: string;
}

export interface ChatResponse {
  success: boolean;
  message: string;
  conversationId: string;
}

export interface ChatHistoryResponse {
  success: boolean;
  data: ChatHistory | null;
  hasHistory: boolean;
}

export interface SendChatMessageResponse {
  success: boolean;
  data: ChatResponse;
  message: string;
}

export interface ReviewVerificationResult {
  keyPointsCovered: {
    covered: string[];
    missing: string[];
    score: number;
  };
  additionalContent: {
    hasAdditional: boolean;
    correctness: number;
    insights: string[];
  };
  overallScore: number;
  feedback: string;
  recommendations: string[];
}

export interface VerifyReviewRequest {
  problemId: string;
  originalNotes: string;
  textInput: string;
  audioTranscription?: string;
}

export interface TranscriptionResponse {
  success: boolean;
  data: {
    transcription: string;
  };
  message: string;
}

class ApiService {
  // Cache for prefetched problems to reduce API calls
  private problemCache: Map<string, Problem> = new Map();
  // Track which pages have been prefetched
  private prefetchedPages: Set<string> = new Set();
  constructor() {
    // Log API configuration in development
    if (CLOUD_FUNCTIONS_URL.includes('localhost')) {
      logger.info('ApiService initialized with Cloud Functions', { baseUrl: CLOUD_FUNCTIONS_URL });
    }
  }

  private getBaseUrl(): string {
    return CLOUD_FUNCTIONS_URL;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    // Get Firebase ID token if user is authenticated
    if (auth.currentUser) {
      try {
        const token = await getIdToken(auth.currentUser);
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        logger.warn('Failed to get auth token', error);
      }
    }

    return headers;
  }
  
  // Deprecated: User info should come from JWT token verification
  // Keeping for backward compatibility during migration
  private getUserHeaders(): Record<string, string> {
    // We no longer send user data in headers - the backend extracts it from JWT
    return {};
  }

  // Build URL for Cloud Functions
  private buildUrl(endpoint: string, method: string = 'GET'): string {
    return this.buildCloudFunctionUrl(endpoint, method);
  }

  private buildCloudFunctionUrl(endpoint: string, method: string = 'GET'): string {
    // Debug logging in development
    if (window.location.hostname === 'localhost') {
      logger.debug('Building Cloud Function URL', { endpoint });
    }
    
    // Extract base path and query parameters
    const [basePath, queryString] = endpoint.split('?');
    const queryParams = queryString ? `?${queryString}` : '';
    
    // Split path into segments for routing
    const segments = basePath.split('/').filter(Boolean);
    
    // Debug logging
    if (window.location.hostname === 'localhost') {
      logger.debug('Route segments', { segments });
    }
    
    if (segments.length === 0) {
      throw new Error(`Invalid route: ${basePath}`);
    }
    
    // Route by first segment
    switch (segments[0]) {
      case 'problems':
        return this.handleProblemsRoute(segments, queryParams, method);
      case 'auth':
        return this.handleAuthRoute(segments, queryParams, method);
      case 'upload':
        return this.handleUploadRoute(segments, queryParams);
      case 'collections':
        return this.handleCollectionsRoute(segments, queryParams, method);
      case 'health':
        return `${CLOUD_FUNCTIONS_URL}/diagnosticHealth${queryParams}`;
      default:
        throw new Error(`Unknown route: ${basePath}`);
    }
  }

  private handleProblemsRoute(segments: string[], queryParams: string, method: string = 'GET'): string {
    if (segments.length === 1) {
      // /problems → getProblems (using cached version)
      return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.getProblems}${queryParams}`;
    }
    
    const secondSegment = segments[1];
    
    // Static endpoints (no ID involved)
    switch (secondSegment) {
      case 'generate':
        // /problems/generate → generateProblem
        const generateUrl = `${CLOUD_FUNCTIONS_URL}/generateProblem${queryParams}`;
        if (window.location.hostname === 'localhost') {
          logger.debug('Problems route: generate', { url: generateUrl });
        }
        return generateUrl;
        
      case 'search-or-generate':
        // /problems/search-or-generate → searchOrGenerate (using cached version)
        return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.searchOrGenerate}${queryParams}`;
        
      case 'stats':
        // /problems/stats → getStats (using cached version)
        return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.getStats}${queryParams}`;
        
      case 'batch':
        // /problems/batch → getProblemsBatch for efficient batch fetching
        const useV2Batch = shouldUseV2Functions();
        return useV2Batch ? `${V2_FUNCTION_URLS.getProblemsBatch}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getProblemsBatch${queryParams}`;
        
      case 'verify-review':
        // /problems/verify-review → verifyReview
        return `${CLOUD_FUNCTIONS_URL}/verifyReview${queryParams}`;
        
      case 'transcribe-audio':
        // /problems/transcribe-audio → transcribeAudio
        const useV2Audio = shouldUseV2Functions();
        return useV2Audio ? `${V2_FUNCTION_URLS.transcribeAudio}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/transcribeAudio${queryParams}`;
        
      case 'by-leetcode':
        // /problems/by-leetcode/{number} → getProblemByLeetcode?number={number}
        if (segments.length === 3) {
          const leetcodeNumber = segments[2];
          return `${CLOUD_FUNCTIONS_URL}/getProblemByLeetcode?number=${leetcodeNumber}`;
        }
        throw new Error(`Invalid by-leetcode route: /${segments.join('/')}`);
        
      case 'batch-by-leetcode':
        // /problems/batch-by-leetcode → getProblemsByLeetcodeNumbers (POST)
        return `${CLOUD_FUNCTIONS_URL}/getProblemsByLeetcodeNumbers${queryParams}`;
        
      case 'generate-by-leetcode':
        // /problems/generate-by-leetcode → generateProblemsByLeetcodeNumbers (POST)
        return `${CLOUD_FUNCTIONS_URL}/generateProblemsByLeetcodeNumbers${queryParams}`;
    }
    
    // Dynamic endpoints with IDs
    if (segments.length === 2) {
      const problemId = secondSegment;
      
      // Route based on HTTP method
      switch (method) {
        case 'GET':
          // GET /problems/{id} → getProblemById/{id} (using cached version)
          return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.getProblemById}/${problemId}${queryParams}`;
        case 'PUT':
          // PUT /problems/{id} → updateProblem/{id} (using cached version)
          return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.updateProblem}/${problemId}${queryParams}`;
        case 'DELETE':
          // DELETE /problems/{id} → deleteProblem/{id} (using cached version)
          return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.deleteProblem}/${problemId}${queryParams}`;
        default:
          // Default to GET behavior
          return `${CLOUD_FUNCTIONS_URL}/${cloudFunctionsConfig.functions.getProblemById}/${problemId}${queryParams}`;
      }
    }
    
    if (segments.length === 3) {
      const problemId = secondSegment;
      const action = segments[2];
      
      switch (action) {
        case 'verify-solution':
          // /problems/{id}/verify-solution → verifySolution/{id}/verify-solution
          return `${CLOUD_FUNCTIONS_URL}/verifySolution/${problemId}/verify-solution${queryParams}`;
          
        case 'generate-language':
          // /problems/{id}/generate-language → generateLanguageCode/{id}/generate-language
          return `${CLOUD_FUNCTIONS_URL}/generateLanguageCode/${problemId}/generate-language${queryParams}`;
          
        case 'generate-animation':
          // /problems/{id}/generate-animation → generateAlgorithmAnimation/{id}/generate-animation
          return `${CLOUD_FUNCTIONS_URL}/generateAlgorithmAnimation/${problemId}/generate-animation${queryParams}`;
          
        case 'animation-url':
          // /problems/{id}/animation-url → getAnimationUrl/{id}/animation-url
          return `${CLOUD_FUNCTIONS_URL}/getAnimationUrl/${problemId}/animation-url${queryParams}`;
          
        case 'generate-walkthrough':
          // /problems/{id}/generate-walkthrough → generateAlgorithmWalkthrough/{id}/generate-walkthrough
          return `${CLOUD_FUNCTIONS_URL}/generateAlgorithmWalkthrough/${problemId}/generate-walkthrough${queryParams}`;
          
        case 'get-walkthroughs':
          // /problems/{id}/get-walkthroughs → getAlgorithmWalkthroughs/{id}/get-walkthroughs
          return `${CLOUD_FUNCTIONS_URL}/getAlgorithmWalkthroughs/${problemId}/get-walkthroughs${queryParams}`;
          
        case 'generation-status':
          // /problems/{id}/generation-status → getProblemGenerationStatus/{id}/generation-status
          // This endpoint checks if a problem is still being generated
          // For now, return a mock endpoint since this function doesn't exist yet
          return `${CLOUD_FUNCTIONS_URL}/getProblemGenerationStatus/${problemId}/generation-status${queryParams}`;
      }
    }
    
    if (segments.length === 4) {
      const problemId = secondSegment;
      const action = segments[2];
      const subAction = segments[3];
      
      if (action === 'generate-language') {
        // /problems/{id}/generate-language/{lang} → generateLanguageCode/{id}/generate-language/{lang}
        return `${CLOUD_FUNCTIONS_URL}/generateLanguageCode/${problemId}/generate-language/${subAction}${queryParams}`;
      }
      
      if (action === 'download-animation') {
        // This case should not be hit anymore since we use query params
        return `${CLOUD_FUNCTIONS_URL}/downloadAlgorithmAnimation/${problemId}/download-animation${queryParams}`;
      }
    }
    
    throw new Error(`Unsupported problems route: /${segments.join('/')}`);
  }

  private handleAuthRoute(segments: string[], queryParams: string, method: string = 'GET'): string {
    if (segments.length === 1) {
      throw new Error(`Invalid auth route: /${segments.join('/')}`);
    }

    const secondSegment = segments[1];

    // Use v2 URLs for deployed environments
    const useV2 = shouldUseV2Functions();
    
    switch (secondSegment) {
      case 'profile':
        // /auth/profile → getUserProfile
        return useV2 ? `${V2_FUNCTION_URLS.getUserProfile}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getUserProfile${queryParams}`;

      case 'preferences':
        // /auth/preferences → updateUserPreferences
        return useV2 ? `${V2_FUNCTION_URLS.updateUserPreferences}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/updateUserPreferences${queryParams}`;

      case 'usage':
        // /auth/usage → getUserUsage
        return `${CLOUD_FUNCTIONS_URL}/getUserUsage${queryParams}`;

      case 'checkout-session':
        // /auth/checkout-session → createCheckoutSession
        return `${CLOUD_FUNCTIONS_URL}/createCheckoutSession${queryParams}`;

      case 'redeem-code':
        // /auth/redeem-code → redeemAccessCode
        return `${CLOUD_FUNCTIONS_URL}/redeemAccessCode${queryParams}`;

      case 'solutions':
        if (segments.length === 2) {
          // /auth/solutions → saveUserSolution (POST) or getUserSolutions (GET)
          return useV2 ? `${V2_FUNCTION_URLS.saveUserSolution}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/saveUserSolution${queryParams}`;
        } else if (segments.length === 4 && segments[3] === 'all') {
          // /auth/solutions/{problemId}/all → getAllUserSolutionsForProblem/{problemId}
          const problemId = segments[2];
          return useV2 ? `${V2_FUNCTION_URLS.getAllUserSolutionsForProblem}/${problemId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getAllUserSolutionsForProblem/${problemId}${queryParams}`;
        } else {
          // /auth/solutions/{problemId} → getUserSolutionByProblem/{problemId}
          const problemId = segments[2];
          return useV2 ? `${V2_FUNCTION_URLS.getUserSolutionByProblem}/${problemId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getUserSolutionByProblem/${problemId}${queryParams}`;
        }
        
      case 'completion-status':
        if (segments.length === 2) {
          // /auth/completion-status → getAllCompletionStatus
          return useV2 ? `${V2_FUNCTION_URLS.getAllCompletionStatus}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getAllCompletionStatus${queryParams}`;
        } else if (segments.length === 3 && segments[2] === 'batch') {
          // /auth/completion-status/batch → batchUpdateCompletionStatus
          return useV2 ? `${V2_FUNCTION_URLS.batchUpdateCompletionStatus}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/batchUpdateCompletionStatus${queryParams}`;
        } else if (segments.length === 3) {
          // /auth/completion-status/{problemId} → getCompletionStatus/{problemId}
          const problemId = segments[2];
          return useV2 ? `${V2_FUNCTION_URLS.getCompletionStatus}/${problemId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getCompletionStatus/${problemId}${queryParams}`;
        } else if (segments.length === 4 && segments[3] === 'toggle') {
          // /auth/completion-status/{problemId}/toggle → toggleCompletionStatus/{problemId}/toggle
          const problemId = segments[2];
          return useV2 ? `${V2_FUNCTION_URLS.toggleCompletionStatus}/${problemId}/toggle${queryParams}` : `${CLOUD_FUNCTIONS_URL}/toggleCompletionStatus/${problemId}/toggle${queryParams}`;
        }
        break;
        
      case 'chat':
        if (segments.length === 2) {
          // /auth/chat → sendChatMessage
          return useV2 ? `${V2_FUNCTION_URLS.sendChatMessage}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/sendChatMessage${queryParams}`;
        } else if (segments.length === 3) {
          // /auth/chat/{problemId} → getChatHistory/{problemId}
          const problemId = segments[2];
          return useV2 ? `${V2_FUNCTION_URLS.getChatHistory}/${problemId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getChatHistory/${problemId}${queryParams}`;
        } else if (segments.length === 4 && segments[3] === 'save') {
          // /auth/chat/{problemId}/save → saveChatHistory/{problemId}/save
          const problemId = segments[2];
          return useV2 ? `${V2_FUNCTION_URLS.saveChatHistory}/${problemId}/save${queryParams}` : `${CLOUD_FUNCTIONS_URL}/saveChatHistory/${problemId}/save${queryParams}`;
        }
        break;

      case 'pins':
        if (segments.length === 2) {
          // GET /auth/pins → getUserPins
          // PUT /auth/pins → updateUserPins
          if (method === 'PUT') {
            return useV2 ? `${V2_FUNCTION_URLS.updateUserPins}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/updateUserPins${queryParams}`;
          }
          return useV2 ? `${V2_FUNCTION_URLS.getUserPins}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getUserPins${queryParams}`;
        } else if (segments.length === 4 && segments[2] === 'collections') {
          // POST/DELETE /auth/pins/collections/{collectionId} → pinCollection / unpinCollection
          const collectionId = segments[3];
          if (method === 'DELETE') {
            return useV2 ? `${V2_FUNCTION_URLS.unpinCollection}/${collectionId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/unpinCollection/${collectionId}${queryParams}`;
          }
          return useV2 ? `${V2_FUNCTION_URLS.pinCollection}/${collectionId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/pinCollection/${collectionId}${queryParams}`;
        } else if (segments.length === 5 && segments[2] === 'problems') {
          // POST/DELETE /auth/pins/problems/{collectionId}/{problemId} → pinProblem / unpinProblem
          const collectionId = segments[3];
          const problemId = segments[4];
          if (method === 'DELETE') {
            return useV2 ? `${V2_FUNCTION_URLS.unpinProblem}/${collectionId}/${problemId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/unpinProblem/${collectionId}/${problemId}${queryParams}`;
          }
          return useV2 ? `${V2_FUNCTION_URLS.pinProblem}/${collectionId}/${problemId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/pinProblem/${collectionId}/${problemId}${queryParams}`;
        }
        break;
    }

    throw new Error(`Unsupported auth route: /${segments.join('/')}`);
  }

  private handleUploadRoute(segments: string[], queryParams: string): string {
    // Use v2 URLs for deployed environments
    const useV2 = shouldUseV2Functions();
    
    if (segments.length === 2 && segments[1] === 'csv') {
      // /upload/csv → uploadCsv
      return useV2 ? `${V2_FUNCTION_URLS.uploadCsv}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/uploadCsv${queryParams}`;
    } else if (segments.length === 3 && segments[1] === 'csv' && segments[2] === 'process') {
      // /upload/csv/process → processCsvBatch
      return useV2 ? `${V2_FUNCTION_URLS.processCsvBatch}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/processCsvBatch${queryParams}`;
    } else if (segments.length === 3 && segments[1] === 'session') {
      // /upload/session/{sessionId} → getUploadSession
      const sessionId = segments[2];
      return useV2 ? `${V2_FUNCTION_URLS.getUploadSession}/${sessionId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getUploadSession/${sessionId}${queryParams}`;
    }
    
    throw new Error(`Unsupported upload route: /${segments.join('/')}`);
  }

  private handleCollectionsRoute(segments: string[], queryParams: string, method: string = 'GET'): string {
    const useV2 = shouldUseV2Functions();
    
    if (segments.length === 1) {
      // /collections with different methods
      if (method === 'POST') {
        // POST /collections → createCollection
        return useV2 ? `${V2_FUNCTION_URLS.createCollection}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/createCollection${queryParams}`;
      }
      // GET /collections → getCollections
      return useV2 ? `${V2_FUNCTION_URLS.getCollections}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getCollections${queryParams}`;
    }
    
    const secondSegment = segments[1];
    
    if (secondSegment === 'system') {
      // /collections/system → getSystemCollections
      return useV2 ? `${V2_FUNCTION_URLS.getSystemCollections}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getSystemCollections${queryParams}`;
    }
    
    if (secondSegment === 'generate') {
      // /collections/generate → generateCollectionByAI
      return useV2 ? `${V2_FUNCTION_URLS.generateCollectionByAI}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/generateCollectionByAI${queryParams}`;
    }
    
    if (secondSegment === 'order') {
      // /collections/order → updateCollectionOrder
      return useV2 ? `${V2_FUNCTION_URLS.updateCollectionOrder}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/updateCollectionOrder${queryParams}`;
    }
    
    if (segments.length === 2) {
      // /collections/{id} with different methods
      const collectionId = secondSegment;
      
      if (method === 'DELETE') {
        // DELETE /collections/{id} → deleteCollection/{id}
        return useV2 ? `${V2_FUNCTION_URLS.deleteCollection}/${collectionId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/deleteCollection/${collectionId}${queryParams}`;
      }
      if (method === 'PUT') {
        // PUT /collections/{id} → updateCollection/{id}
        return useV2 ? `${V2_FUNCTION_URLS.updateCollection}/${collectionId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/updateCollection/${collectionId}${queryParams}`;
      }
      // GET /collections/{id} → getCollection/{id}
      return useV2 ? `${V2_FUNCTION_URLS.getCollection}/${collectionId}${queryParams}` : `${CLOUD_FUNCTIONS_URL}/getCollection/${collectionId}${queryParams}`;
    }
    
    if (segments.length === 3) {
      const collectionId = secondSegment;
      const action = segments[2];

      if (action === 'problems') {
        // PATCH /collections/{id}/problems → updateCollectionProblems/{id}/problems
        return useV2 ? `${V2_FUNCTION_URLS.updateCollectionProblems}/${collectionId}/problems${queryParams}` : `${CLOUD_FUNCTIONS_URL}/updateCollectionProblems/${collectionId}/problems${queryParams}`;
      }

      if (action === 'sequence') {
        // PUT /collections/{id}/sequence → updateProblemSequence/{id}/sequence
        return useV2 ? `${V2_FUNCTION_URLS.updateProblemSequence}/${collectionId}/sequence${queryParams}` : `${CLOUD_FUNCTIONS_URL}/updateProblemSequence/${collectionId}/sequence${queryParams}`;
      }
    }

    throw new Error(`Unsupported collections route: /${segments.join('/')}`);
  }

  async request<T>(
    endpoint: string,
    options?: RequestInit & { timeout?: number }
  ): Promise<ApiResponse<T>> {
    const method = options?.method || 'GET';
    const url = this.buildUrl(endpoint, method);
    const timer = logger.startTimer(`API ${method} ${endpoint}`);

    // Debug logging
    logger.debug(`Making ${method} request`, {
      url,
      endpoint,
      method,
      hasBody: !!options?.body,
      timeout: options?.timeout
    });

    try {
      const { timeout, ...fetchOptions } = options || {};

      // Add correlation headers from logger
      const correlationHeaders = logger.getRequestHeaders();

      // Get auth headers (now async for Firebase tokens)
      const authHeaders = await this.getAuthHeaders();

      // Create a fetch request with custom timeout if specified
      const fetchWithTimeout = (url: string, options: RequestInit, timeoutMs?: number) => {
        if (!timeoutMs) {
          return fetch(url, options);
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        return fetch(url, {
          ...options,
          signal: controller.signal,
        }).finally(() => {
          clearTimeout(timeoutId);
        });
      };

      const response = await fetchWithTimeout(url, {
        ...fetchOptions,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          ...correlationHeaders,
          ...fetchOptions?.headers,
        },
      }, timeout);

      const duration = timer();
      
      // Log API response
      logger.trackAPI(method, endpoint, response.status, duration);

      // Handle 204 No Content response
      if (response.status === 204) {
        return { success: true };
      }

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 - Token might be expired
        if (response.status === 401 && auth.currentUser) {
          logger.info('Token expired, refreshing...');

          // Force refresh token
          const newToken = await getIdToken(auth.currentUser, true);

          // Retry request with new token
          const retryResponse = await fetchWithTimeout(url, {
            ...fetchOptions,
            headers: {
              "Content-Type": "application/json",
              'Authorization': `Bearer ${newToken}`,
              ...correlationHeaders,
              ...fetchOptions?.headers,
            },
          }, timeout);

          if (!retryResponse.ok) {
            const retryData = await retryResponse.json();
            const error = new Error(retryData.error || `HTTP error! status: ${retryResponse.status}`);
            (error as any).status = retryResponse.status;
            (error as any).data = retryData;
            throw error;
          }

          const retryData = await retryResponse.json();
          return retryData;
        }

        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        // Add status code to error for better handling
        (error as any).status = response.status;
        (error as any).data = data;
        throw error;
      }

      return data;
    } catch (error: any) {
      const { userMessage, errorCode, technical } = UserMessages.fromError(error);
      
      // Log the error with both user and technical messages
      logger.userError(
        userMessage,
        `API request failed: ${technical}`,
        error as Error
      );
      
      // Throw an error with user-friendly message
      const userError = new Error(userMessage);
      (userError as any).code = errorCode;
      (userError as any).originalError = error;
      throw userError;
    }
  }

  async getProblems(
    params: GetProblemsRequest = {}
  ): Promise<PaginatedResponse<Problem>> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.tags?.length) queryParams.append("tags", params.tags.join(","));
    if (params.difficulty?.length)
      queryParams.append("difficulty", params.difficulty.join(","));
    if (params.search) queryParams.append("search", params.search);
    if (params.company) queryParams.append("company", params.company);

    const response = await this.request<any>(
      `/problems?${queryParams.toString()}`
    );

    // Handle v2 response format
    // The v2 response might be wrapped: { success: true, data: [...], pagination: {...} }
    // Or it might be double-wrapped if there's an extra layer
    const typedResponse = response as any;
    
    // Check if the response has the expected structure
    if (typedResponse && typedResponse.pagination && typedResponse.data) {
      return {
        data: typedResponse.data || [],
        page: typedResponse.pagination.page,
        limit: typedResponse.pagination.limit,
        total: typedResponse.pagination.total,
        hasNext: typedResponse.pagination.hasNext,
        hasPrev: typedResponse.pagination.hasPrev
      };
    }
    
    // Check if response is double-wrapped (response.data contains the actual response)
    if (typedResponse && typedResponse.data && typedResponse.data.pagination) {
      const actualData = typedResponse.data;
      return {
        data: actualData.data || [],
        page: actualData.pagination.page,
        limit: actualData.pagination.limit,
        total: actualData.pagination.total,
        hasNext: actualData.pagination.hasNext,
        hasPrev: actualData.pagination.hasPrev
      };
    }

    // Fallback for unexpected format
    return {
      data: [],
      page: 1,
      limit: 20,
      total: 0,
      hasNext: false,
      hasPrev: false
    };
  }

  async getProblemById(id: string): Promise<Problem> {
    // Check cache first
    const cached = this.problemCache.get(id);
    if (cached) {
      logger.debug('Cache hit for problem', { problemId: id });
      return cached;
    }
    
    const response = await this.request<Problem>(`/problems/${id}`);
    
    // Handle Cloud Functions response format
    if (response.data) {
      // Cache the problem for future use
      this.problemCache.set(id, response.data);
      return response.data;
    }
    
    return response.data!;
  }

  // New batch fetching method
  async getProblemsBatch(problemIds: string[]): Promise<Problem[]> {
    if (problemIds.length === 0) return [];
    
    // Separate cached and uncached IDs
    const uncachedIds: string[] = [];
    const cachedProblems: Problem[] = [];
    
    for (const id of problemIds) {
      const cached = this.problemCache.get(id);
      if (cached) {
        cachedProblems.push(cached);
      } else {
        uncachedIds.push(id);
      }
    }
    
    logger.debug('Batch fetch stats', { cached: cachedProblems.length, toFetch: uncachedIds.length });
    
    // If all are cached, return immediately
    if (uncachedIds.length === 0) {
      return cachedProblems;
    }
    
    try {
      // Use the new batch endpoint
      const response = await this.request<Problem[]>('/problems/batch', {
        method: 'POST',
        body: JSON.stringify({ problemIds: uncachedIds })
      });
      
      const fetchedProblems = response.data || [];
      
      // Cache the fetched problems
      fetchedProblems.forEach(problem => {
        this.problemCache.set(problem.id, problem);
      });
      
      // Combine cached and newly fetched problems
      const allProblems = [...cachedProblems, ...fetchedProblems];
      
      // Sort to maintain original ID order
      const idOrder = new Map(problemIds.map((id, index) => [id, index]));
      allProblems.sort((a, b) => {
        const aIndex = idOrder.get(a.id) ?? Infinity;
        const bIndex = idOrder.get(b.id) ?? Infinity;
        return aIndex - bIndex;
      });
      
      return allProblems;
    } catch (error) {
      logger.error('Batch fetch failed, falling back to individual fetches', error as Error, {
        endpointUrl: this.buildUrl('/problems/batch', 'POST'),
        requestBody: { problemIds: uncachedIds }
      });
      console.error('Error details:', error instanceof Error ? error.message : error);
      
      // Fallback to individual fetches
      const problemPromises = uncachedIds.map(id => 
        this.getProblemById(id).catch(err => {
          console.error(`Failed to fetch problem ${id}:`, err);
          return null;
        })
      );
      
      const fetchedProblems = await Promise.all(problemPromises);
      const validFetched = fetchedProblems.filter((p): p is Problem => p !== null);
      
      return [...cachedProblems, ...validFetched];
    }
  }

  // Prefetch next pages in the background
  private async prefetchNextPages(collectionId: string, problemIds: string[], currentPage: number, pageSize: number) {
    // Mark as prefetched to avoid duplicate prefetching
    this.prefetchedPages.add(collectionId);
    
    // Calculate IDs for next 4 pages (pages 2-5)
    const pagesToPrefetch = 4;
    const prefetchIds: string[] = [];
    
    for (let i = 1; i <= pagesToPrefetch; i++) {
      const pageNum = currentPage + i;
      const startIndex = (pageNum - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const pageIds = problemIds.slice(startIndex, endIndex);
      
      if (pageIds.length === 0) break;
      prefetchIds.push(...pageIds);
    }
    
    if (prefetchIds.length > 0) {
      console.log(`🚀 Prefetching ${prefetchIds.length} problems for pages 2-5...`);
      
      // Prefetch in background (don't await)
      this.getProblemsBatch(prefetchIds)
        .then(problems => {
          console.log(`✅ Prefetched ${problems.length} problems successfully`);
        })
        .catch(error => {
          console.error('❌ Prefetch failed:', error);
        });
    }
  }

  // Clear cache method (useful for memory management)
  clearProblemCache() {
    this.problemCache.clear();
    this.prefetchedPages.clear();
    console.log('🗑️ Problem cache cleared');
  }

  async getProblemGenerationStatus(problemId: string): Promise<{
    status: 'pending' | 'generating' | 'completed' | 'failed';
    attempts?: number;
    lastError?: string;
    createdAt?: Date;
    startedAt?: Date;
  }> {
    const response = await this.request<any>(`/problems/${problemId}/generation-status`);
    return response.data!;
  }

  async getProblemByLeetcode(leetcodeNumber: number): Promise<Problem | null> {
    try {
      const response = await this.request<Problem>(`/problems/by-leetcode/${leetcodeNumber}`);
      return response.data || null;
    } catch (error: any) {
      // Check both direct status and originalError status for 404
      if (error.status === 404 || error.originalError?.status === 404) {
        return null; // Problem not found
      }
      throw error;
    }
  }

  async getProblemsByLeetcodeNumbers(leetcodeNumbers: number[]): Promise<{
    success: boolean;
    data: Map<number, Problem | null>;
    found: number[];
    notFound: number[];
    message: string;
  }> {
    console.log('getProblemsByLeetcodeNumbers called with:', leetcodeNumbers);
    
    const response = await this.request<{
      data: { [key: string]: Problem | null };
      found: number[];
      notFound: number[];
      message: string;
    }>('/problems/batch-by-leetcode', {
      method: 'POST',
      body: JSON.stringify({ numbers: leetcodeNumbers }),
    });

    console.log('Batch API raw response:', response);

    // The response.data contains the actual response from the service
    const serviceResponse = response.data;
    
    // Convert the object response to a Map
    const problemsMap = new Map<number, Problem | null>();
    if (serviceResponse?.data && typeof serviceResponse.data === 'object' && !Array.isArray(serviceResponse.data)) {
      Object.entries(serviceResponse.data as { [key: string]: Problem | null }).forEach(([key, value]) => {
        problemsMap.set(parseInt(key), value);
      });
    }

    const result = {
      success: true,  // If we got here, the request was successful
      data: problemsMap,
      found: serviceResponse?.found || [],
      notFound: serviceResponse?.notFound || [],
      message: serviceResponse?.message || ''
    };
    
    console.log('Batch API processed result:', result);
    return result;
  }

  async generateProblemsByLeetcodeNumbers(
    leetcodeNumbers: number[]
  ): Promise<BatchGenerationResult> {
    console.log('🤖 generateProblemsByLeetcodeNumbers called with:', leetcodeNumbers);
    console.log(`📝 Generating ${leetcodeNumbers.length} problems by LeetCode numbers`);
    
    const response = await this.request<BatchGenerationResult>('/problems/generate-by-leetcode', {
      method: 'POST',
      body: JSON.stringify({ numbers: leetcodeNumbers }),
      timeout: 300000, // 5 minutes timeout for batch generation
    });

    console.log('✅ Batch generation response:', {
      generated: Object.keys(response.data?.generated || {}).length,
      existing: Object.keys(response.data?.existing || {}).length,
      failed: response.data?.failed?.length || 0
    });

    return response.data!;
  }

  async generateProblem(request: GenerateProblemRequest): Promise<Problem> {
    console.log('🤖 generateProblem called with:', request);
    console.log('🔗 About to call /problems/generate endpoint');
    
    const response = await this.request<Problem>("/problems/generate", {
      method: "POST",
      body: JSON.stringify(request),
      timeout: 120000, // 120 seconds timeout for AI generation (claude-opus can take 70+ seconds)
    });

    return response.data!;
  }

  async searchOrGenerate(query: string, difficulty?: "Easy" | "Medium" | "Hard"): Promise<PaginatedResponse<Problem> & { type?: 'search' | 'generate' }> {
    const response = await this.request<any>('/problems/search-or-generate', {
      method: "POST",
      body: JSON.stringify({ query, ...(difficulty && { difficulty }) }),  // Only include difficulty if specified
      timeout: 120000, // 120 seconds timeout for potential AI generation (claude-opus can take 70+ seconds)
    });

    // Backend returns type: 'search' or 'generate' to indicate if problem was found or generated
    return response.data! as PaginatedResponse<Problem> & { type?: 'search' | 'generate' };
  }

  async updateProblem(id: string, updates: Partial<Problem>): Promise<Problem> {
    const response = await this.request<Problem>(`/problems/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });

    return response.data!;
  }

  async deleteProblem(id: string): Promise<void> {
    await this.request(`/problems/${id}`, {
      method: "DELETE",
    });
  }

  async getStats(): Promise<ProblemStats> {
    const response = await this.request<any>("/problems/stats");
    
    // Standardized response format
    // Response structure: { success: true, data: { total, byDifficulty, topTags, ... } }
    const typedResponse = response as any;
    if (typedResponse && typedResponse.data) {
      const stats = typedResponse.data;
      return {
        totalProblems: stats.total || 0,
        difficultyStats: stats.byDifficulty || { Easy: 0, Medium: 0, Hard: 0 },
        topTags: stats.topTags || [],
        averageFrequency: stats.averageFrequency || 0
      };
    }
    
    // Fallback
    return {
      totalProblems: 0,
      difficultyStats: { Easy: 0, Medium: 0, Hard: 0 },
      topTags: [],
      averageFrequency: 0
    };
  }

  async healthCheck(): Promise<any> {
    const healthUrl = this.buildUrl('/health');
    const response = await fetch(healthUrl);
    return response.json();
  }

  async verifySolution(problemId: string, request: VerifySolutionRequest): Promise<SolutionFeedback> {
    const response = await this.request<SolutionFeedback>(`/problems/${problemId}/verify-solution`, {
      method: "POST",
      body: JSON.stringify(request),
    });

    return response.data!;
  }

  async uploadCsv(file: File): Promise<CsvUploadResponse> {
    const formData = new FormData();
    formData.append('csv', file);

    const url = this.buildUrl('/upload/csv');
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getAuthHeaders(), // Add auth headers for Cloud Functions
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    // Backend returns nested response: { success, data: { sessionId, totalRows, status }, message }
    // Frontend expects flat response: { success, sessionId, totalRows, message }
    return {
      success: data.success,
      sessionId: data.data.sessionId,
      totalRows: data.data.totalRows,
      message: data.message
    };
  }

  async processCsvBatch(request: CsvProcessBatchRequest): Promise<CsvProcessBatchResponse> {
    const url = this.buildUrl('/upload/csv/process');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify(request),
      // No timeout needed - Cloud Function handles it with 540s timeout
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    // Backend now returns direct response matching CsvProcessBatchResponse interface
    return data;
  }

  async saveUserSolution(problemId: string, request: SaveUserSolutionRequest): Promise<UserSolution> {
    const response = await this.request<any>(`/auth/solutions`, {
      method: "POST",
      body: JSON.stringify({
        problemId,
        solution: request  // Wrap request in 'solution' property to match backend expectation
      }),
    });
    // Handle v2 response format
    if (response.data && 'success' in response.data && response.data.data) {
      return response.data.data;
    }
    return response.data!;
  }

  async getUserSolutions(): Promise<UserSolution[]> {
    const response = await this.request<any>(`/auth/solutions`);
    // Handle v2 response format
    if (response.data && 'success' in response.data && response.data.data) {
      return response.data.data;
    }
    return response.data!;
  }

  async getUserSolutionForProblem(problemId: string): Promise<UserSolution | null> {
    try {
      console.log('API: Making request for solution:', problemId);
      const response = await this.request<any>(`/auth/solutions/${problemId}`);
      console.log('API: Raw response:', response);
      console.log('API: Response data:', response.data);
      // Handle v2 response format
      if (response.data && 'success' in response.data && response.data.data) {
        return response.data.data;
      }
      return response.data!;
    } catch (error) {
      console.log('API: Error getting solution:', error);
      // Return null if solution not found
      return null;
    }
  }

  async getAllUserSolutionsForProblem(problemId: string): Promise<UserSolution[]> {
    try {
      console.log('API: Making request for all solutions:', problemId);
      const response = await this.request<any>(`/auth/solutions/${problemId}/all`);
      console.log('API: Raw response for all solutions:', response);
      // Handle v2 response format
      if (response.data && 'success' in response.data && response.data.data) {
        return response.data.data;
      }
      return response.data || [];
    } catch (error) {
      console.log('API: Error getting all solutions:', error);
      return [];
    }
  }

  // Completion Status Methods
  async toggleCompletionStatus(problemId: string): Promise<any> {
    const response = await this.request<any>(`/auth/completion-status/${problemId}/toggle`, {
      method: "POST"
    });
    // Backend returns the response directly with success, data, message
    return response;
  }

  async getCompletionStatus(problemId: string): Promise<any> {
    const response = await this.request<any>(`/auth/completion-status/${problemId}`);
    // Backend returns the response directly with success, data, isCompleted
    return response;
  }

  async getAllCompletionStatus(): Promise<Record<string, boolean>> {
    const response = await this.request<any>(`/auth/completion-status`);
    // Backend returns { success: true, data: UserCompletionStatus[] }
    // Convert array to Record<problemId, isCompleted>
    const completionMap: Record<string, boolean> = {};
    if (response.data && Array.isArray(response.data)) {
      response.data.forEach((status: any) => {
        if (status.problemId && status.isCompleted !== undefined) {
          completionMap[status.problemId] = status.isCompleted;
        }
      });
    }
    return completionMap;
  }

  /**
   * Batch update completion status for multiple problems
   * Used for "Mark All Completed" feature
   */
  async batchUpdateCompletionStatus(
    problemIds: string[],
    isCompleted: boolean
  ): Promise<{ updated: number; created: number; problemIds: string[] }> {
    const response = await this.request<any>(`/auth/completion-status/batch`, {
      method: "POST",
      body: JSON.stringify({ problemIds, isCompleted }),
    });
    // Backend returns { success: true, data: { updated, created, problemIds } }
    return response.data || { updated: 0, created: 0, problemIds: [] };
  }

  async getCompletedProblems(company?: string): Promise<{ data: Problem[]; total: number }> {
    let url = '/auth/completed-problems';
    const queryParams = new URLSearchParams();
    
    if (company) {
      queryParams.append('company', company);
    }
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }
    
    const response = await this.request<{ data: Problem[]; total: number }>(url);
    return response.data!;
  }

  async getUserCompletedProblems(): Promise<any> {
    const response = await this.request<any>(`/auth/completion-status`);
    // Backend returns the response directly with success, data, count
    return response;
  }

  // Chat Methods
  async getChatHistory(problemId: string): Promise<any> {
    const response = await this.request<any>(`/auth/chat/${problemId}`);
    // Backend returns the response directly with success, data, hasHistory
    return response;
  }

  async sendChatMessage(problemId: string, message: string): Promise<any> {
    const response = await this.request<any>(`/auth/chat`, {
      method: "POST",
      body: JSON.stringify({ message, problemId }),
    });
    // Backend returns the response directly with success, data, message
    return response;
  }

  async saveChatHistory(problemId: string): Promise<ApiResponse> {
    const response = await this.request<ApiResponse>(`/auth/chat/${problemId}/save`, {
      method: "POST",
    });
    return response;
  }

  async hasChatHistory(problemId: string): Promise<boolean> {
    try {
      const response = await this.getChatHistory(problemId);
      return response.success && response.hasHistory && response.data && response.data.messages.length > 0;
    } catch (error) {
      console.error('Error checking chat history:', error);
      return false;
    }
  }

  async transcribeAudio(audioFile: File): Promise<string> {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const url = this.buildUrl('/problems/transcribe-audio');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(await this.getAuthHeaders()),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data.data.transcription;
  }

  async verifyReview(request: VerifyReviewRequest): Promise<ReviewVerificationResult> {
    const response = await this.request<ReviewVerificationResult>('/problems/verify-review', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return response.data!;
  }

  async generateLanguageCode(problemId: string, language: 'typescript' | 'java' | 'golang'): Promise<Array<{name: string; code: string; explanation?: string}>> {
    console.log(`🔄 Generating ${language} code for problem ${problemId}`);
    
    const response = await this.request<{implementations: Array<{name: string; code: string; explanation?: string}>}>(`/problems/${problemId}/generate-language`, {
      method: 'POST',
      body: JSON.stringify({ language }),
    });

    console.log(`✅ Generated ${language} code for problem ${problemId}`);
    return response.data!.implementations;
  }

  async generateAlgorithmAnimation(problemId: string, approachName: string): Promise<string> {
    console.log(`🎬 [API] generateAlgorithmAnimation called`);
    console.log(`🎬 [API] Problem ID: ${problemId}`);
    console.log(`🎬 [API] Approach Name: ${approachName}`);
    
    try {
      const url = this.buildUrl(`/problems/${problemId}/generate-animation`);
      console.log(`🌐 [API] Built URL: ${url}`);
      
      const requestBody = { approachName };
      console.log(`📦 [API] Request body:`, requestBody);
      
      const response = await this.request<{signedUrl: string, fileName: string}>(`/problems/${problemId}/generate-animation`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        timeout: 360000, // 360 seconds (6 minutes) timeout for AI generation
      });

      console.log(`✅ [API] Response received:`, {
        success: response.success,
        hasData: !!response.data,
        hasSignedUrl: !!(response.data?.signedUrl),
        fileName: response.data?.fileName
      });
      
      if (!response.data?.signedUrl) {
        throw new Error('No signed URL received from server');
      }

      console.log(`✅ [API] Generated animation for ${approachName} on problem ${problemId}`);
      return response.data.signedUrl;
    } catch (error) {
      console.error(`❌ [API] Failed to generate animation:`, error);
      throw error;
    }
  }

  async getAnimationUrl(problemId: string, approachName: string): Promise<string> {
    console.log(`🔗 [API] Getting animation URL for approach: ${approachName} on problem ${problemId}`);
    
    try {
      // Use query parameter for approach name
      const queryParams = new URLSearchParams();
      queryParams.append('approach', approachName);
      
      const response = await this.request<{signedUrl: string, fileName: string}>(`/problems/${problemId}/animation-url?${queryParams.toString()}`);
      
      console.log(`✅ [API] Response received:`, {
        success: response.success,
        hasData: !!response.data,
        hasSignedUrl: !!(response.data?.signedUrl),
      });
      
      if (!response.data?.signedUrl) {
        throw new Error('No signed URL received from server');
      }
      
      console.log(`✅ [API] Got animation URL for ${approachName} animation`);
      return response.data.signedUrl;
    } catch (error) {
      console.error(`❌ [API] Failed to get animation URL:`, error);
      throw error;
    }
  }

  getApiUrl(): string {
    return this.getBaseUrl();
  }

  // Auth Methods - Updated for Firebase
  async getUserProfile(): Promise<any> {
    const response = await this.request<any>('/auth/profile', {
      method: 'GET'
    });
    // Handle v2 response format
    if (response.data && 'success' in response.data && response.data.data) {
      return response.data.data;
    }
    return response.data;
  }

  async updateUserPreferences(preferredLanguage: string): Promise<any> {
    const response = await this.request<any>('/auth/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferredLanguage }),
    });
    // Handle v2 response format
    if (response.data && 'success' in response.data && response.data.data) {
      return response.data.data;
    }
    return response.data!;
  }

  /**
   * Get User Usage Summary
   * Fetches current usage state including credits, status, and trial info
   */
  async getUserUsage(): Promise<any> {
    const response = await this.request<any>('/auth/usage', {
      method: 'GET'
    });

    // Response format: { success: true, data: UsageSummary }
    return response;
  }

  /**
   * Redeem Access Code
   * Allows users to redeem a promotional code for Pro access
   */
  async redeemAccessCode(code: string): Promise<{
    success: boolean;
    data?: {
      planId: string;
      creditsGranted: number;
      expiresAt: string | null;
      message: string;
    };
    error?: string;
    code?: string;
  }> {
    const response = await this.request<{
      planId: string;
      creditsGranted: number;
      expiresAt: string | null;
      message: string;
    }>('/auth/redeem-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });

    return response;
  }

  async generateAlgorithmWalkthrough(problemId: string, approachName: string): Promise<AlgorithmWalkthrough> {
    console.log(`🚶 [API] Generating walkthrough for approach: ${approachName} on problem ${problemId}`);
    
    try {
      const response = await this.request<AlgorithmWalkthrough>(`/problems/${problemId}/generate-walkthrough`, {
        method: 'POST',
        body: JSON.stringify({ approachName }),
      });
      
      console.log(`✅ [API] Generated walkthrough for ${approachName} on problem ${problemId}`);
      return response.data!;
    } catch (error) {
      console.error(`❌ [API] Failed to generate walkthrough:`, error);
      throw error;
    }
  }

  async getAlgorithmWalkthroughs(problemId: string, approachName: string): Promise<AlgorithmWalkthrough[]> {
    console.log(`🚶 [API] Getting walkthroughs for approach: ${approachName} on problem ${problemId}`);
    
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('approach', approachName);
      
      const response = await this.request<AlgorithmWalkthrough[]>(`/problems/${problemId}/get-walkthroughs?${queryParams.toString()}`);
      
      console.log(`✅ [API] Retrieved ${response.data?.length || 0} walkthroughs for ${approachName}`);
      return response.data || [];
    } catch (error) {
      console.error(`❌ [API] Failed to get walkthroughs:`, error);
      throw error;
    }
  }

  // Collections Methods
  async getCollections(userId?: string): Promise<any> {
    // User ID is now extracted from JWT token on backend
    // The userId parameter is deprecated but kept for backward compatibility
    const response = await this.request<any>('/collections?type=user');
    // Backend returns { success: true, data: [...], count: number }
    // We need to return the data directly
    return response.data || response;
  }

  async getSystemCollections(): Promise<any> {
    const response = await this.request<any>('/collections/system');
    // Backend returns { success: true, data: [...], count: number }
    // We need to return the data directly
    return response.data || response;
  }

  async getCollection(id: string): Promise<any> {
    const response = await this.request<any>(`/collections/${id}?includeProblems=false`);
    // Backend returns { success: true, data: { collection: {...}, problems?: [...] } }
    // We need to return the data object which contains collection
    return response.data || response;
  }

  async createCollection(data: any): Promise<any> {
    console.log('Creating collection with data:', data);
    const response = await this.request<any>('/collections', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    console.log('Create collection response:', response);
    // Backend returns { success: true, data: collection }
    return response.success === false ? null : (response.data || response);
  }

  async updateCollection(id: string, data: any): Promise<any> {
    const response = await this.request<any>(`/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    // Backend returns { success: true, data: collection }
    return response.success === false ? null : (response.data || response);
  }

  async deleteCollection(id: string): Promise<void> {
    await this.request(`/collections/${id}`, {
      method: 'DELETE'
    });
  }

  async updateCollectionProblems(collectionId: string, action: 'add' | 'remove', problemIds: string[]): Promise<void> {
    await this.request(`/collections/${collectionId}/problems`, {
      method: 'PATCH',
      body: JSON.stringify({
        action,
        problemIds
      })
    });
  }

  async updateUserCollectionOrder(userId: string, order: Record<string, number>): Promise<void> {
    await this.request(`/users/${userId}/collection-order`, {
      method: 'PUT',
      body: JSON.stringify({ collectionOrder: order })
    });
  }

  async updateProblemSequence(collectionId: string, problemSequence: string[]): Promise<any> {
    const response = await this.request<any>(`/collections/${collectionId}/sequence`, {
      method: 'PUT',
      body: JSON.stringify({ problemSequence })
    });
    // Backend returns { success: true, data: collection }
    return response.success === false ? null : (response.data || response);
  }

  async generateCollectionByAI(data: any): Promise<any> {
    console.log('🤖 Generating collection by AI with data:', data);

    // ✅ Client-side auth check for better UX (avoid wasted API call)
    if (!auth.currentUser) {
      throw new Error('Authentication required. Please sign in to generate collections.');
    }

    try {
      const response = await this.request<any>('/collections/generate', {
        method: 'POST',
        body: JSON.stringify(data),
        timeout: 120000 // 2 minutes timeout for AI generation
      });
      console.log('AI generation raw response:', response);
      
      // Backend returns { success: true, data: collection }
      if (response.success === false) {
        console.error('AI generation failed:', response);
        throw new Error(response.error || response.message || 'Failed to generate collection');
      }
      
      const collection = response.data || response;
      console.log('AI generation extracted collection:', collection);
      
      // Validate the collection has an ID
      if (!collection || !collection.id) {
        console.error('AI generated collection missing ID:', collection);
        throw new Error('Generated collection is missing ID field');
      }
      
      return collection;
    } catch (error) {
      console.error('AI generation error:', error);
      // ✅ No need to check auth error here - it's checked upfront
      throw error;
    }
  }


  async getCollectionProblems(collectionId: string, problemIds: string[], page = 1, pageSize = 20): Promise<Problem[]> {
    // When pageSize is -1, load all problems (no pagination)
    const loadAll = pageSize === -1;
    let idsToFetch: string[];
    
    if (loadAll) {
      idsToFetch = problemIds;
      logger.info(`📚 Loading ALL ${problemIds.length} problems for collection ${collectionId}`);
    } else {
      // Calculate which problem IDs to fetch based on pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      idsToFetch = problemIds.slice(startIndex, endIndex);
      logger.info(`📚 Loading problems for collection ${collectionId}, page ${page}, fetching ${idsToFetch.length} problems`);
      
      // Prefetch next 4 pages (total 5 pages including current) on first page load
      if (page === 1 && !this.prefetchedPages.has(collectionId)) {
        this.prefetchNextPages(collectionId, problemIds, page, pageSize);
      }
    }
    
    if (idsToFetch.length === 0) {
      return [];
    }

    // Use the new batch endpoint for efficient fetching
    logger.info('📚 Using batch API to fetch problems...');
    const problems = await this.getProblemsBatch(idsToFetch);
    
    logger.info(`✅ Successfully loaded ${problems.length}/${idsToFetch.length} problems`);

    return problems;
  }

  // Pin Methods
  /**
   * GET /auth/pins - Get user's pin preferences
   */
  async getUserPins(): Promise<any> {
    const response = await this.request<any>('/auth/pins', {
      method: 'GET'
    });
    return response.data || response;
  }

  /**
   * PUT /auth/pins - Update user's pin preferences (bulk update)
   */
  async updateUserPins(pinData: any): Promise<any> {
    const response = await this.request<any>('/auth/pins', {
      method: 'PUT',
      body: JSON.stringify(pinData)
    });
    return response.data || response;
  }

  /**
   * POST /auth/pins/collections/:collectionId - Pin a collection
   */
  async pinCollection(collectionId: string): Promise<any> {
    const response = await this.request<any>(`/auth/pins/collections/${collectionId}`, {
      method: 'POST'
    });
    return response.data || response;
  }

  /**
   * DELETE /auth/pins/collections/:collectionId - Unpin a collection
   */
  async unpinCollection(collectionId: string): Promise<any> {
    const response = await this.request<any>(`/auth/pins/collections/${collectionId}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }

  /**
   * POST /auth/pins/problems/:collectionId/:problemId - Pin a problem
   */
  async pinProblem(collectionId: string, problemId: string): Promise<any> {
    const response = await this.request<any>(`/auth/pins/problems/${collectionId}/${problemId}`, {
      method: 'POST'
    });
    return response.data || response;
  }

  /**
   * DELETE /auth/pins/problems/:collectionId/:problemId - Unpin a problem
   */
  async unpinProblem(collectionId: string, problemId: string): Promise<any> {
    const response = await this.request<any>(`/auth/pins/problems/${collectionId}/${problemId}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }
}

export const apiService = new ApiService();
